<!--
// nuxt-ui/components/pages/region-world-cup/BracketTablesWorldCup.vue
-->
<script setup lang="ts">
import type { BracketData } from "~/src/world-cup/domain/models/Bracket";
import MatchItemBracketTablesWorldCup from "~/nuxt-ui/components/pages/region-world-cup/MatchItemBracketTablesWorldCup.vue";
import IconArrowBracketTablesWorldCup from "~/nuxt-ui/components/pages/region-world-cup/IconArrowBracketTablesWorldCup.vue";
import BracketWorldCupDatasource from "~/src/world-cup/datasources/WorldCupBracket.json";
import { useSeasons } from "~/nuxt-ui/Shared/composables/season/seasons";
import { useTeams } from "~/nuxt-ui/Shared/composables/teams";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";
import { arrowsSliderVisibility, isPastDate } from "~/src/Shared/utils";

type Props = {
  selectedSeason: number;
};

const props = defineProps<Props>();

const { getSeasonTurns, getSeasonTeams } = useSeasons();
const { teamArrToObjById } = useTeams();
const { currentSeasonTeams, currentSeason } = useCompetitionStore();
const { locale } = useI18n();

const bracketWorldCupData: BracketData = BracketWorldCupDatasource;
const seasonMatches = await getSeasonTurns(props.selectedSeason);
const teamsObj = ref();

if (currentSeason.id === props.selectedSeason) {
  teamsObj.value = await teamArrToObjById(currentSeasonTeams, props.selectedSeason);
} else {
  const teams = await getSeasonTeams(props.selectedSeason);
  teamsObj.value = await teamArrToObjById(teams, props.selectedSeason);
}

const findMatchInSeason = (turnIndex: number, matchId: number) => {
  if (!seasonMatches) return undefined;
  const seasonMatchesValues = Object.values(seasonMatches);
  return seasonMatchesValues[turnIndex].find(m => m.id === matchId);
};

const round16Active = isPastDate("2024-06-03T09:00:00.000Z");
const quarterFinalsActive = isPastDate("2024-06-06T09:00:00.000Z");
const finalsActive = isPastDate("2024-06-07T10:00:00.000Z");

const trophyImage = "/img/world-cup/kwc-trophy.png";

onMounted(() => {
  const tablesContainer: HTMLElement | null = document.getElementById("tablesSlider");
  const bracketButtonLeft: HTMLElement | null = document.getElementById("bracketButtonLeft");
  const bracketButtonRight: HTMLElement | null = document.getElementById("bracketButtonRight");

  if (tablesContainer) {
    let childrenTablesContainerWidth: number = 0;
    const childrenTablesContainer = [...tablesContainer.children];
    childrenTablesContainer.forEach(item => (childrenTablesContainerWidth += item.clientWidth));
    const swingWrapper = tablesContainer.querySelector(".swing");

    const stagesBracket: NodeListOf<HTMLElement> = tablesContainer.querySelectorAll(
      ".round-col-container.active-stage"
    );
    const lastStagePlayed: HTMLElement | null = stagesBracket[stagesBracket.length - 1];
    tablesContainer.scrollLeft = 0;

    if (lastStagePlayed && tablesContainer) {
      tablesContainer.scrollLeft = lastStagePlayed.getBoundingClientRect().x - 80;
    }

    if (swingWrapper) {
      if (
        Math.round(tablesContainer.scrollLeft) ===
        Math.round(tablesContainer.scrollWidth) - Math.round(tablesContainer.clientWidth)
      )
        //if is totally right
        swingWrapper.classList.remove("swing");
    }

    tablesContainer.addEventListener("scroll", () => {
      if (swingWrapper) {
        swingWrapper.classList.remove("swing");
      }
      arrowsSliderVisibility("general", tablesContainer, bracketButtonLeft, bracketButtonRight);
    });
  }
});
</script>

<template>
  <div
    v-if="Object.keys(seasonMatches).length"
    id="tablesSlider"
    class="w-full overflow-x-scroll overflow-y-hidden hide-scrollbar bigms:px-4"
  >
    <div class="grid grid-flow-col auto-cols-max gap-20 swing" style="scale: 0.8">
      <!--Redemption-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.redemption") }}</h3>
        <div class="round-container kwc-border-color self-stretch">
          <match-item-bracket-tables-world-cup
            v-for="match in bracketWorldCupData.redemption"
            :key="match.matchId"
            :match="findMatchInSeason(0, match.matchId)"
            :teams-obj="teamsObj"
            :bracket-match-info="match"
          ></match-item-bracket-tables-world-cup>
        </div>
      </div>

      <!--Round 1-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.round1") }}</h3>
        <div class="round-container-cols-2 kwc-border-color">
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-world-cup
              v-for="match in bracketWorldCupData.round1.slice(0, 8)"
              :key="match.matchId"
              :match="findMatchInSeason(1, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
            ></match-item-bracket-tables-world-cup>
          </div>
          <div class="flex items-stretch justify-center px-5">
            <div class="w-full"></div>
            <div class="w-[2px] kwc-divider"></div>
          </div>
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-world-cup
              v-for="match in bracketWorldCupData.round1.slice(8, 16)"
              :key="match.matchId"
              :match="findMatchInSeason(1, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
            ></match-item-bracket-tables-world-cup>
          </div>

          <icon-arrow-bracket-tables-world-cup
            width-class="w-[calc(5rem-8px)]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--Round 2-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.round2") }}</h3>
        <div class="round-container-cols-2 kwc-border-color !mb-20 self-stretch">
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-world-cup
              v-for="match in bracketWorldCupData.round2Winners.slice(0, 4)"
              :key="match.matchId"
              :match="findMatchInSeason(2, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
            ></match-item-bracket-tables-world-cup>
          </div>
          <div class="flex items-stretch justify-center px-5">
            <div class="w-full"></div>
            <div class="w-[2px] kwc-divider"></div>
          </div>
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-world-cup
              v-for="match in bracketWorldCupData.round2Winners.slice(4, 8)"
              :key="match.matchId"
              :match="findMatchInSeason(2, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
            ></match-item-bracket-tables-world-cup>
          </div>

          <icon-arrow-bracket-tables-world-cup
            width-class="w-[calc(5rem-8px)]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            text-indicator="1-0"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[calc(5rem-8px)]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
            text-indicator="1-1"
            right
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="p-[2px] linear-gradient-red self-stretch">
          <div class="round-container-cols-2 !border-none bg-black">
            <div class="flex flex-col items-stretch gap-4">
              <match-item-bracket-tables-world-cup
                v-for="match in bracketWorldCupData.round2Losers.slice(0, 4)"
                :key="match.matchId"
                :match="findMatchInSeason(2, match.matchId)"
                :teams-obj="teamsObj"
                :bracket-match-info="match"
              ></match-item-bracket-tables-world-cup>
            </div>
            <div class="flex items-stretch justify-center px-5">
              <div class="w-full"></div>
              <div class="w-[2px] kwc-divider"></div>
            </div>
            <div class="flex flex-col items-stretch gap-4">
              <match-item-bracket-tables-world-cup
                v-for="match in bracketWorldCupData.round2Losers.slice(4, 8)"
                :key="match.matchId"
                :match="findMatchInSeason(2, match.matchId)"
                :teams-obj="teamsObj"
                :bracket-match-info="match"
              ></match-item-bracket-tables-world-cup>
            </div>

            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#7E0900"
              top-position-class="top-1/2"
              text-indicator="0-1"
            ></icon-arrow-bracket-tables-world-cup>
            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#7E0900"
              top-position-class="top-1/2"
              text-indicator="1-1"
              right
            ></icon-arrow-bracket-tables-world-cup>
            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#7E0900"
              top-position-class="bottom-0"
              text-indicator="0-2"
              right
            ></icon-arrow-bracket-tables-world-cup>
          </div>
        </div>
      </div>

      <!--Last Chance-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.lastChance") }}</h3>
        <div class="p-[2px] linear-gradient-red my-auto">
          <div class="round-container !border-none bg-black">
            <match-item-bracket-tables-world-cup
              v-for="match in bracketWorldCupData.lastChance"
              :key="match.matchId"
              :match="findMatchInSeason(3, match.matchId)"
              :teams-obj="teamsObj"
              :bracket-match-info="match"
              name-no-bold
            ></match-item-bracket-tables-world-cup>

            <icon-arrow-bracket-tables-world-cup
              width-class="w-[66px]"
              bg-color="#7E0900"
              top-position-class="top-full"
              text-indicator="1-2"
              vertical
            ></icon-arrow-bracket-tables-world-cup>
          </div>
        </div>
        <div
          class="self-stretch linear-gradient-red font-['Archivo_Expanded'] flex justify-center items-center p-2"
        >
          OUT
        </div>
      </div>

      <!--octavos-->
      <div class="round-col-container" :class="{ 'active-stage': round16Active }">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.roundOf16") }}</h3>
        <div class="p-[2px] linear-gradient-gold-red my-auto self-stretch">
          <div class="round-container !border-none bg-black">
            <div class="flex flex-col items-stretch gap-4 pb-4 mb-20">
              <match-item-bracket-tables-world-cup
                v-for="match in bracketWorldCupData.roundOf16.slice(0, 4)"
                :key="match.matchId"
                :match="findMatchInSeason(4, match.matchId)"
                :teams-obj="teamsObj"
                :bracket-match-info="match"
                name-no-bold
              ></match-item-bracket-tables-world-cup>
            </div>

            <div class="flex flex-col items-stretch gap-4 pt-4">
              <match-item-bracket-tables-world-cup
                v-for="match in bracketWorldCupData.roundOf16.slice(4, 8)"
                :key="match.matchId"
                :match="findMatchInSeason(4, match.matchId)"
                :teams-obj="teamsObj"
                :bracket-match-info="match"
                name-no-bold
              ></match-item-bracket-tables-world-cup>
            </div>

            <icon-arrow-bracket-tables-world-cup
              width-class="w-[500px]"
              bg-color="#D4B78E"
              top-position-class="top-2"
              text-indicator="2-0"
            ></icon-arrow-bracket-tables-world-cup>
            <icon-arrow-bracket-tables-world-cup
              width-class="w-[calc(5rem-8px)]"
              bg-color="#AE8957"
              top-position-class="top-1/2"
              text-indicator="2-1"
              is-gradient-bg="linear-gradient(90deg, #5C010A 0%, #DBBE93 50%, #AE8957 100%)"
            ></icon-arrow-bracket-tables-world-cup>
          </div>
        </div>
      </div>

      <!--cuartos-->
      <div class="round-col-container" :class="{ 'active-stage': quarterFinalsActive }">
        <h3 :class="['round-title kwc-color max-w-32', { '!-mb-1': locale === 'en' }]">
          {{ $t("bracketKwc.turnsTitle.quarterFinals") }}
        </h3>
        <div class="round-container kwc-border-color">
          <match-item-bracket-tables-world-cup
            v-for="match in bracketWorldCupData.quarterFinals"
            :key="match.matchId"
            :match="findMatchInSeason(5, match.matchId)"
            :teams-obj="teamsObj"
            :bracket-match-info="match"
            name-no-bold
          ></match-item-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[calc(5rem-8px)]"
            bg-color="#AE8957"
            is-gradient-bg="linear-gradient(70deg, #DE8564 0%, #DBBE93 51%, #AE8957 100%)"
            top-position-class="top-1/2"
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--semis-->
      <div class="round-col-container">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.semifinals") }}</h3>
        <div class="round-container kwc-border-color">
          <match-item-bracket-tables-world-cup
            v-for="match in bracketWorldCupData.semifinals"
            :key="match.matchId"
            :match="findMatchInSeason(6, match.matchId)"
            :teams-obj="teamsObj"
            :bracket-match-info="match"
            name-no-bold
          ></match-item-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[85px]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--final-->
      <div class="round-col-container" :class="{ 'active-stage': finalsActive }">
        <h3 class="round-title kwc-color">{{ $t("bracketKwc.turnsTitle.final") }}</h3>
        <div class="round-container kwc-border-color">
          <img
            :src="trophyImage"
            alt="Trophy KWC"
            width="180"
            height="200"
            class="absolute bottom-[91%] self-center"
          />
          <match-item-bracket-tables-world-cup
            v-for="match in bracketWorldCupData.final"
            :key="match.matchId"
            :match="findMatchInSeason(6, match.matchId)"
            :teams-obj="teamsObj"
            :bracket-match-info="match"
            name-no-bold
          ></match-item-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            width-class="w-[85px]"
            bg-color="#D4B78E"
            top-position-class="top-1/2"
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.round-title {
  @apply font-['Termina'] font-semibold text-xl text-center uppercase mb-6;
}

.round-container {
  @apply border-2 px-5 py-7 flex flex-col items-stretch gap-4 my-auto relative bg-black;
}

.round-container-cols-2 {
  @apply border-2 px-5 py-7 gap-4 my-auto grid grid-cols-[1fr_auto_1fr] relative justify-items-stretch bg-black;
}

.round-col-container {
  @apply flex flex-col justify-stretch items-center;
}

.linear-gradient-red {
  background-image: linear-gradient(90deg, #54000a 0%, #b70007 32%, #d2283d 51%, #b70007 70%, #54000a 100%);
}

.linear-gradient-gold-red {
  background-image: linear-gradient(180deg, #ae8957 0%, #e0ba86 25%, #dbbe93 48%, #e21607 70%, #870006 100%);
}

.kwc-divider {
  background-image: repeating-linear-gradient(
    175deg,
    #d4b78e,
    #d4b78e 12px,
    transparent 12px,
    transparent 24px,
    #d4b78e 22px
  );
}

.swing {
  position: relative;
  animation: swing 5s 5 ease-in-out;
}

@keyframes swing {
  18% {
    transform: translateX(0);
  }
  24% {
    transform: translateX(-20px);
  }
  30% {
    transform: translateX(0);
  }
  36% {
    transform: translateX(-20px);
  }
  42% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
